import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Upload } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type { RefCallBack } from 'react-hook-form';
import { CreatableSelect } from 'chakra-react-select';
import { useDebounce } from 'use-debounce';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { cleanUnitCode } from '../reviewedRequirements';
import { NrtRegisterUpload } from './NrtRegisterUpload';

type UnitCodeOption = {
  label: ReactNode;
  value: string;
};

function unitLabel(unit: { code: string; title: string }): ReactNode {
  return (
    <HStack spacing={2}>
      <Badge colorScheme="blue" fontSize="xs" flexShrink={0} fontFamily="mono">
        {unit.code}
      </Badge>
      <Text fontSize="sm" noOfLines={1}>
        {unit.title}
      </Text>
    </HStack>
  );
}

export function AccreditedUnitCodeField({
  value,
  onChange,
  inputRef,
  canUpdateRegister,
  errorMessage,
}: {
  value: string;
  onChange: (unitCode: string) => void;
  inputRef: RefCallBack;
  canUpdateRegister: boolean;
  errorMessage?: string;
}) {
  const [showUpload, setShowUpload] = useState(false);
  const [unitSearch, setUnitSearch] = useState('');
  const [debouncedUnitSearch] = useDebounce(unitSearch, 500);

  const { data: searchResults = [], isFetching: isSearching } =
    trpcAdmin.unitsOfCompetency.searchNrtRegister.useQuery(debouncedUnitSearch || undefined, {
      enabled: debouncedUnitSearch.length >= 2,
    });

  const { data: nrtLookup } = trpcAdmin.unitsOfCompetency.lookupNrtCode.useQuery(value, {
    enabled: value !== '',
  });

  const unitOptions = useMemo(
    () => searchResults.map((unit) => ({ value: unit.code, label: unitLabel(unit) })),
    [searchResults],
  );

  const selectedOption: UnitCodeOption | null =
    value === ''
      ? null
      : { value, label: nrtLookup?.code === value ? unitLabel(nrtLookup) : value };

  return (
    <FormControl isRequired isInvalid={errorMessage != null}>
      <HStack justify="space-between" align="baseline">
        <FormLabel htmlFor="unitCode" mb={0}>
          <Trans>Unit Code</Trans>
        </FormLabel>
        {canUpdateRegister ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            color="fg.subtle"
            fontWeight="normal"
            onClick={() => setShowUpload((prev) => !prev)}
          >
            <HStack spacing={1}>
              <Upload size={12} />
              <Text>
                <Trans>Update NRT register</Trans>
              </Text>
            </HStack>
          </Button>
        ) : null}
      </HStack>

      <CreatableSelect<UnitCodeOption, false>
        ref={inputRef}
        inputId="unitCode"
        placeholder={t`Search for a unit code (e.g. BSBWHS411)`}
        options={unitOptions}
        isLoading={isSearching}
        onInputChange={(next) => setUnitSearch(next)}
        filterOption={null}
        isClearable
        formatCreateLabel={(inputValue: string) => `Use "${inputValue}"`}
        value={selectedOption}
        onChange={(option) => onChange(option?.value ?? '')}
        onCreateOption={(inputValue: string) => onChange(cleanUnitCode(inputValue))}
        selectedOptionColorScheme="brand"
        chakraStyles={{
          container: (provided) => ({ ...provided, w: '100%' }),
        }}
      />

      {errorMessage != null ? (
        <FormErrorMessage>{errorMessage}</FormErrorMessage>
      ) : value !== '' && nrtLookup === null ? (
        <Alert status="warning" borderRadius="md" mt={2} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            <Trans>
              Code not found in the NRT register. Try updating the register if it's out of date.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : nrtLookup != null && nrtLookup.type !== 'Unit of competency' ? (
        <Alert status="error" borderRadius="md" mt={2} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            <Trans>
              {value} is a {nrtLookup.type.toLowerCase()}. Please enter a unit of competency code.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : null}

      {showUpload ? <NrtRegisterUpload /> : null}
    </FormControl>
  );
}
